import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import "./AdminPanel.css";

const MASTER_WIDTH = 1200;
const MASTER_HEIGHT = 1800;
const SLICE_WIDTH = 1200;
const SLICE_HEIGHT = 600;
const SEGMENT_TYPES = ["TOP", "MIDDLE", "BOTTOM"];

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob(
    (blob) => blob ? resolve(blob) : reject(new Error("Bild konnte nicht erzeugt werden")),
    "image/webp",
    0.92,
  );
});

function detectMasterBands() {
  return [
    { type: "TOP", y: 0, height: SLICE_HEIGHT },
    { type: "MIDDLE", y: SLICE_HEIGHT, height: SLICE_HEIGHT },
    { type: "BOTTOM", y: SLICE_HEIGHT * 2, height: SLICE_HEIGHT },
  ];
}

async function normalizeBand(rawBand) {
  const canvas = document.createElement("canvas");
  canvas.width = SLICE_WIDTH;
  canvas.height = SLICE_HEIGHT;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Preserve the artist-approved close crop exactly. Frame bars and the
  // fixed 2:1 viewports provide the separation; per-motif zooming would
  // change the intended composition.
  ctx.drawImage(
    rawBand,
    0,
    0,
    rawBand.width,
    rawBand.height,
    0,
    0,
    SLICE_WIDTH,
    SLICE_HEIGHT,
  );
  return canvas;
}

async function prepareMaster(file) {
  const bitmap = await createImageBitmap(file);
  const ratio = bitmap.width / bitmap.height;
  const expectedRatio = MASTER_WIDTH / MASTER_HEIGHT;

  if (Math.abs(ratio - expectedRatio) > 0.01) {
    bitmap.close();
    throw new Error(`${file.name}: ${bitmap.width}×${bitmap.height} ist nicht im 2:3-Format`);
  }

  const master = document.createElement("canvas");
  master.width = MASTER_WIDTH;
  master.height = MASTER_HEIGHT;
  const masterCtx = master.getContext("2d");
  masterCtx.imageSmoothingEnabled = true;
  masterCtx.imageSmoothingQuality = "high";
  masterCtx.drawImage(bitmap, 0, 0, MASTER_WIDTH, MASTER_HEIGHT);
  bitmap.close();

  const bands = detectMasterBands(master);
  const slices = [];

  for (const band of bands) {
    const rawBand = document.createElement("canvas");
    rawBand.width = MASTER_WIDTH;
    rawBand.height = band.height;
    const rawCtx = rawBand.getContext("2d");
    rawCtx.drawImage(master, 0, band.y, MASTER_WIDTH, band.height, 0, 0, MASTER_WIDTH, band.height);

    const normalized = await normalizeBand(rawBand);
    slices.push({ type: band.type, blob: await canvasToBlob(normalized) });
  }

  return slices;
}

export default function MasterImport() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const inputRef = useRef();
  const [sets, setSets] = useState([]);
  const [setId, setSetId] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [pendingMasters, setPendingMasters] = useState([]);

  useEffect(() => {
    if (ready && !user) nav("/admin/login");
  }, [ready, user, nav]);

  const loadSets = useCallback(async () => {
    const { data } = await api.get("/admin/sets");
    setSets(data);
    if (data.length) setSetId((current) => current || data[0].id);
  }, []);

  useEffect(() => {
    if (ready && user) loadSets();
  }, [ready, user, loadSets]);

  useEffect(() => () => {
    pendingMasters.forEach((master) => {
      master.slices.forEach((slice) => URL.revokeObjectURL(slice.previewUrl));
    });
  }, [pendingMasters]);

  const uploadSlice = async ({ blob, segmentType, slot, figureId }) => {
    const fd = new FormData();
    fd.append("segment_type", segmentType);
    fd.append("slot_number", slot);
    fd.append("name", figureId);
    fd.append("file", blob, `${figureId}-${segmentType.toLowerCase()}.webp`);
    await api.post(`/admin/sets/${setId}/assets`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const prepareMasters = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!setId) return toast.error("Bitte zuerst ein Set wählen");
    if (!files.length) return;
    if (files.length > 10) return toast.error("Maximal 10 Masterbilder auf einmal");

    setBusy(true);
    setLastResult(null);
    const prepared = [];
    try {
      for (let i = 0; i < files.length; i += 1) {
        setProgress(`Bereite Master ${i + 1}/${files.length} für die Rahmenvorschau vor …`);
        const slices = await prepareMaster(files[i]);
        prepared.push({
          slot: i + 1,
          figureId: `FIGURE-${String(i + 1).padStart(2, "0")}`,
          slices: slices.map((slice) => ({ ...slice, previewUrl: URL.createObjectURL(slice.blob) })),
        });
      }
      setPendingMasters(prepared);
      toast.success("Vorschau fertig. Bitte alle drei Bereiche vor dem Speichern prüfen.");
    } catch (err) {
      prepared.forEach((master) => master.slices.forEach((slice) => URL.revokeObjectURL(slice.previewUrl)));
      toast.error(err?.message || "Master-Vorschau fehlgeschlagen");
    } finally {
      setBusy(false);
      setProgress("");
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const savePreparedMasters = async () => {
    if (!pendingMasters.length) return;
    setBusy(true);
    setLastResult(null);
    try {
      for (let i = 0; i < pendingMasters.length; i += 1) {
        const master = pendingMasters[i];
        setProgress(`Speichere ${master.figureId} (${i + 1}/${pendingMasters.length}) …`);
        for (const slice of master.slices) {
          await uploadSlice({ blob: slice.blob, segmentType: slice.type, slot: master.slot, figureId: master.figureId });
        }
      }

      const result = { masters: pendingMasters.length, slices: pendingMasters.length * 3 };
      setLastResult(result);
      setPendingMasters([]);
      toast.success(`${result.masters} Masterbilder · ${result.slices} Slices gespeichert`);
    } catch (err) {
      toast.error(err?.message || "Master-Import fehlgeschlagen");
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  if (!ready || !user) return null;

  return (
    <div className="admin-shell admin-import-page min-h-[100dvh] px-4 py-5">
      <div className="max-w-5xl mx-auto">
      <header className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-serif-display text-brass text-2xl">Adminbereich</h1>
          <p className="font-mono-meta text-[10px] text-stone-500 uppercase tracking-widest">Master-Import</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => nav("/")} className="border-stone-700 text-stone-300">Zur Website</Button>
      </header>

      <div className="bg-surface border border-stone-800 rounded-lg p-1 mb-4 flex flex-wrap gap-1">
        <Button variant="ghost" size="sm" onClick={() => nav("/admin?tab=overview")} className="text-stone-300">Übersicht</Button>
        <Button variant="ghost" size="sm" onClick={() => nav("/admin?tab=sets")} className="text-stone-300">Sets</Button>
        <Button variant="ghost" size="sm" onClick={() => nav("/admin?tab=assets")} className="text-stone-300">Motive</Button>
        <Button size="sm" className="cta-brass">Master-Import</Button>
        <Button variant="ghost" size="sm" onClick={() => nav("/admin?tab=matrix")} className="text-stone-300">Kombinationen</Button>
        <Button variant="ghost" size="sm" onClick={() => nav("/admin?tab=requests")} className="text-stone-300">Anfragen</Button>
      </div>

      <div className="bg-surface border border-stone-800 rounded-xl p-4 space-y-4">
        <div>
          <Label className="text-stone-400 text-xs">Ziel-Set</Label>
          <Select value={setId} onValueChange={setSetId} disabled={busy}>
            <SelectTrigger className="bg-canvas border-stone-700 mt-1 max-w-md"><SelectValue placeholder="Set wählen" /></SelectTrigger>
            <SelectContent>{sets.map((set) => <SelectItem key={set.id} value={set.id}>{set.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="rounded-xl border border-stone-800 bg-canvas p-4">
          <h2 className="font-cormorant text-xl text-brass">Automatische Motivtrennung</h2>
          <p className="text-sm text-stone-300 mt-2">
            TattooGen normalisiert das gesamte 2:3-Masterbild auf 1200×1800 und teilt es exakt bei 600 und 1200 Pixel.
            Die freigegebenen großen Motivausschnitte werden unverändert übernommen; es gibt keinen zusätzlichen Zoom und keine automatische Verkleinerung.
          </p>
          <div className="grid sm:grid-cols-3 gap-2 mt-4 text-xs">
            {SEGMENT_TYPES.map((type) => (
              <div key={type} className="border border-stone-800 rounded-lg p-3">
                <strong>{type}</strong><div className="text-stone-500">exakter Ausschnitt · Ausgabe 1200×600</div>
              </div>
            ))}
          </div>
        </div>

        <Button type="button" className="cta-brass rounded-full" disabled={busy || !setId} onClick={() => inputRef.current?.click()} data-testid="master-import-button">
          <Upload size={16} className="mr-1" />{busy ? "Verarbeitung läuft …" : "Bis zu 10 Masterbilder auswählen"}
        </Button>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={(e) => prepareMasters(e.target.files)} />

        {busy && <div className="font-mono-meta text-xs text-amber-400">{progress}</div>}

        {pendingMasters.length > 0 && (
          <section className="rounded-xl border border-stone-700 bg-canvas p-4 space-y-4">
            <div>
              <h3 className="font-cormorant text-xl text-brass">Rahmenvorschau vor dem Speichern</h3>
              <p className="text-xs text-stone-400 mt-1">Prüfe, ob jedes Motiv vollständig sichtbar und in seinem Bereich sauber zentriert ist.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingMasters.map((master) => (
                <article key={master.figureId}>
                  <div className="font-mono-meta text-[10px] text-stone-400 mb-2">{master.figureId} · Slot {master.slot}</div>
                  <div className="relative w-full aspect-[2/3] bg-stone-100 overflow-hidden">
                    {master.slices.map((slice, index) => (
                      <img
                        key={slice.type}
                        src={slice.previewUrl}
                        alt={`${master.figureId} ${slice.type}`}
                        className="absolute object-cover"
                        style={{
                          left: "12.5%",
                          width: "75%",
                          height: "25%",
                          top: ["13.7%", "40.5%", "67.6%"][index],
                        }}
                      />
                    ))}
                    <img src="/dystopia-frame-web.webp" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full pointer-events-none" />
                  </div>
                </article>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" className="cta-brass" disabled={busy} onClick={savePreparedMasters}>Vorschau bestätigen und speichern</Button>
              <Button type="button" variant="outline" disabled={busy} onClick={() => setPendingMasters([])}>Verwerfen</Button>
            </div>
          </section>
        )}

        {lastResult && <div className="flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle2 size={17} /> {lastResult.masters} Masterbilder · {lastResult.slices} Slices erfolgreich gespeichert</div>}
      </div>
      </div>
    </div>
  );
}
